import type {ReactNode} from 'react';
import React from 'react';

import {Heading, Icon, Text} from '@albert-io/atomic';

import type {QuestionModelV3, QuestionSetModelV1, SubjectModelV2} from '@albert-io/models';

import {QuestionWrapper, HeadingBar} from 'client/components/QuestionTypes/shared/ContentBlocks';

import {RESTRICTED_QUESTION_TITLE, STUDENT_RESTRICTION_MESSAGE} from './studentContentLock';

interface NavigationProps {
  disabled: boolean;
  handleNavigation: () => void;
}

interface Props {
  actions?: ReactNode;
  activeQuestion: QuestionModelV3;
  activeQuestionSet: QuestionSetModelV1;
  activeSubject: SubjectModelV2;
  headingActions: ReactNode;
  navigateBackProps: NavigationProps | null;
  navigateNextProps: NavigationProps | null;
}

/**
 * Block-and-message panel shown to a student in place of a restricted question inside an assignment
 * that has not been lock-authorized (admin-locked-assessments, Part 4). Swapped in for the whole
 * question renderer by `QuestionRenderer`, so the content is never revealed: there is no stem, no
 * answer input, and no explanation or graded/results state, and the question is non-interactive. The
 * student continues to the next question with the existing Next action; surrounding non-restricted
 * questions render normally.
 *
 * Mirrors the existing `BlockedAssignmentQuestion` shell, but redacts the header title via
 * `modifyTitle` — `QuestionWrapper` injects the real `activeQuestion` into the cloned `HeadingBar`,
 * so without `modifyTitle` the real title would render in the header. Uses neutral, school-less copy
 * — students are not shown the restricting school (`references/ui-patterns.md`). Final copy and
 * visual treatment are design-TBD (the open AC item in `part-04-student-experience-locked/ac.md`).
 */
export default function BlockedForStudentQuestion({
  activeQuestion,
  activeQuestionSet,
  activeSubject,
  actions,
  navigateBackProps,
  navigateNextProps,
  headingActions
}: Props) {
  return (
    <QuestionWrapper
      question={activeQuestion}
      questionSet={activeQuestionSet}
      subject={activeSubject}
      heading={<HeadingBar question={undefined} questionSet={undefined} showDifficulty={false} />}
      // `QuestionWrapper` clones the heading with its own `showDifficulty`, overriding the value set
      // on the inline `HeadingBar`; set it here so the redacted header doesn't leak difficulty (#17843).
      showDifficulty={false}
      modifyTitle={() => RESTRICTED_QUESTION_TITLE}
      navigateBackProps={navigateBackProps}
      navigateNextProps={navigateNextProps}
      headingActions={headingActions}
    >
      <div className='flex flex-col items-center p-x6'>
        <div className='flex w-full max-w-[480px] flex-col gap-x2'>
          <Heading as='h2' size='m'>
            <Icon
              icon='shield'
              iconStyle='solid'
              color='negative'
              size='inherit'
              className='mr-x1'
            />
            {STUDENT_RESTRICTION_MESSAGE}
          </Heading>
          <Text>
            Restricted questions cannot be answered unless an admin specifically authorizes this
            assignment. You can continue answering unrestricted questions in this assignment.
          </Text>
        </div>
      </div>
      {actions || null}
    </QuestionWrapper>
  );
}
